import React from "react";
import {
  Alert,
  Box,
  Button,
  ProgressBar,
  SpaceBetween,
  StatusIndicator,
} from "@amzn/awsui-components-react";
import { OmsExportRequest } from "../../lusca-search-api/generated-src";
import useOmsExport from "../../hooks/useOmsExport";
import { fileSize } from "./omsReportFormatting";

export type OmsExportState = ReturnType<typeof useOmsExport>;
export function OMSExportActions({
  state,
  mode,
  filters = [],
  operation = "and",
  valid = true,
}: {
  state: OmsExportState;
  mode: OmsExportRequest["mode"];
  filters?: OmsExportRequest["filters"];
  operation?: OmsExportRequest["operation"];
  valid?: boolean;
}) {
  const job = state.job.data;
  if (state.expired || state.unavailable)
    return (
      <Button variant="primary" onClick={state.reset}>
        Create new export
      </Button>
    );
  if (state.createConflict && !state.jobId)
    return (
      <Button variant="primary" onClick={state.reset}>
        Start new export
      </Button>
    );
  if (state.job.isError && state.jobId)
    return (
      <SpaceBetween direction="horizontal" size="xs">
        <Button onClick={state.reset}>Start new export</Button>
        <Button
          variant="primary"
          loading={state.job.isFetching}
          loadingText="Checking export"
          onClick={() => {
            state.job.refetch().catch(() => undefined);
          }}
        >
          Retry status
        </Button>
      </SpaceBetween>
    );
  if (job?.status === "READY")
    return (
      <SpaceBetween direction="horizontal" size="xs">
        <Button onClick={state.reset}>
          {mode === "full" || (job.csvFiles || 1) > 1
            ? "Create new export"
            : "Change filters"}
        </Button>
        <Button
          variant="primary"
          iconName="download"
          wrapText={false}
          loading={state.download.isLoading}
          loadingText="Preparing download"
          onClick={() => state.download.mutate()}
        >
          Download ZIP
        </Button>
      </SpaceBetween>
    );
  if (job?.status === "FAILED")
    return (
      <Button variant="primary" onClick={state.reset}>
        Start new export
      </Button>
    );
  if (state.jobId)
    return (
      <Button variant="primary" disabled>
        Creating export
      </Button>
    );
  return (
    <Button
      variant="primary"
      wrapText={false}
      loading={state.create.isLoading}
      loadingText="Starting export"
      disabled={!valid}
      onClick={() => state.create.mutate({ filters, operation })}
    >
      Create export
    </Button>
  );
}
export default function OMSExportPanel({
  state,
  mode,
}: {
  state: OmsExportState;
  mode: OmsExportRequest["mode"];
}) {
  const job = state.job.data;
  if (state.expired || state.unavailable)
    return (
      <Alert
        type="info"
        header={state.expired ? "Export expired" : "Export no longer available"}
      >
        Create a new export to download this report.
      </Alert>
    );
  const createError = state.create.error as {
    response?: { status?: number; data?: { error?: string; message?: string } };
  } | null;
  let createMessage = "Choose Create export again to retry.";
  if (state.createConflict) {
    createMessage = "Choose Start new export to try again with a new request.";
  } else if (createError?.response?.status === 400) {
    createMessage =
      createError.response.data?.error ||
      createError.response.data?.message ||
      "Check your filters, then choose Create export again.";
  }
  return (
    <SpaceBetween size="s">
      {state.create.isError && (
        <Alert type="error" header="Could not start export">
          {createMessage}
        </Alert>
      )}
      {state.job.isError && state.jobId && (
        <Alert type="error" header="Export status unavailable">
          Your export may still be running. Choose Retry status to check.
        </Alert>
      )}
      {state.jobId && !job && !state.job.isError && (
        <div role="status">Checking export status…</div>
      )}
      {(job?.status === "PREPARING" || state.create.isLoading) && (
        <ProgressBar
          value={
            job?.totalParts ? (job.completedParts / job.totalParts) * 100 : 0
          }
          label="Creating your export"
          description="Your export continues if you close this window."
          additionalInfo={
            job && job.completedParts === job.totalParts
              ? "Finalising ZIP…"
              : undefined
          }
        />
      )}
      {job?.status === "FAILED" && (
        <Alert type="error" header="Export could not be completed">
          {job.error || "Start a new export to try again."}
        </Alert>
      )}
      {job?.status === "READY" && (
        <SpaceBetween size="xs">
          <div className="oms-report-download__result" role="status">
            <StatusIndicator type="success">Export ready</StatusIndicator>
            <span>
              {job.rowCount?.toLocaleString()} rows ·{" "}
              {fileSize(job.sizeBytes || 0)} ZIP
            </span>
          </div>
          {job.rowCount === 0 && (
            <Box>
              {mode === "full" || job.sourceRows === 0
                ? "Empty report. The CSV contains headers only."
                : "No matching rows. The CSV contains headers only."}
            </Box>
          )}
          <Box color="text-body-secondary">
            Download available until{" "}
            {new Intl.DateTimeFormat(undefined, {
              year: "numeric",
              month: "short",
              day: "numeric",
              hour: "numeric",
              minute: "2-digit",
              timeZoneName: "short",
            }).format(new Date(job.expiresAt))}
            .
          </Box>
        </SpaceBetween>
      )}
      {state.download.isError && (
        <Alert type="error" header="Could not start download">
          Choose Download ZIP again to get a fresh download link.
        </Alert>
      )}
      {state.sent && (
        <div role="status" aria-live="polite">
          <Box color="text-body-secondary">Sent to browser downloads.</Box>
        </div>
      )}
    </SpaceBetween>
  );
}
